import { type ReactNode, type RefObject, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router';
import { LinienSymbol } from './linien-symbol.js';
import stile from './seitenkopf.module.css';

/**
 * Kopfzeile einer Seite wie `Seite` in der Content-Factory (components/seite/seite.tsx): der
 * Weg zurueck klein ueber dem Titel, darunter genau EIN h1, beides auf einer haftenden Leiste
 * mit Linie darunter. Die Leiste laeuft ueber die volle Breite des Inhalts. Wie dort traegt sie
 * optional einen Satz unter dem Titel (`beschreibung`) und rechts die eine Aktion der Seite.
 */
export function Seitenkopf({
  titel,
  pfad = [],
  beschreibung,
  aktion,
}: {
  titel: string;
  /** Weg zurueck, ohne die aktuelle Seite selbst. */
  pfad?: readonly { text: string; ziel: string }[];
  /** Ein Satz, der sagt, was hier zu tun ist. */
  beschreibung?: ReactNode;
  aktion?: ReactNode;
}) {
  const leiste = useRef<HTMLDivElement>(null);
  useHoeheAlsRand(leiste);
  return (
    <div ref={leiste} className={stile.leiste} data-seitenkopf="">
      <div className={stile.links}>
        {pfad.length > 0 && (
          <nav className={stile.weg} aria-label="Weg zurück">
            {pfad.map((punkt) => (
              <span key={punkt.ziel} className={stile.wegpunkt}>
                <Link to={punkt.ziel}>{punkt.text}</Link>
                <LinienSymbol name="chevron-right" groesse={13} />
              </span>
            ))}
          </nav>
        )}
        <h1 className={stile.titel}>{titel}</h1>
        {beschreibung && <p className={stile.beschreibung}>{beschreibung}</p>}
      </div>
      {aktion && <div className={stile.aktion}>{aktion}</div>}
    </div>
  );
}

/**
 * Traegt die Hoehe des haftenden Kopfes als `--awf-seitenkopf-hoehe` ein, damit haftende Spalten
 * (Entscheidung, Freigabe) und Sprungziele darunter landen statt dahinter. Gemessen statt
 * geschaetzt: der Satz unter dem Titel bricht je nach Breite ein- oder zweizeilig um.
 */
function useHoeheAlsRand(leiste: RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => {
    const element = leiste.current;
    const wurzel = document.documentElement;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const beobachter = new ResizeObserver(() => {
      wurzel.style.setProperty('--awf-seitenkopf-hoehe', `${element.offsetHeight}px`);
    });
    beobachter.observe(element);
    return () => {
      beobachter.disconnect();
      wurzel.style.removeProperty('--awf-seitenkopf-hoehe');
    };
  }, [leiste]);
}
